import { motion } from 'framer-motion';
import type { InputHTMLAttributes, JSX, ReactNode } from 'react';
import { LivingBackdrop } from '../features/onboarding/LivingBackdrop';

/**
 * Centered glassy card used by every auth screen. M0 "First Breath": a {@link LivingBackdrop} sits
 * BEHIND the card so the world is already breathing before the newcomer signs in. The card stays
 * above it (`relative z-10`) and keeps its blur/translucent background so the form is always legible.
 */
export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="relative flex h-full w-full items-center justify-center p-4 sm:p-6">
      <LivingBackdrop />
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        // Fluid card: never wider than a comfortable reading column, never taller than the viewport —
        // on short landscape tablets (e.g. 1024×768) the register form scrolls INSIDE the card rather
        // than pushing its buttons off-screen. Padding eases up on the smallest viewports.
        className="relative z-10 flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-y-auto rounded-3xl border border-white/10 bg-slate-950/60 p-6 shadow-2xl backdrop-blur-xl sm:p-8"
      >
        <div className="mb-6 text-center">
          <div className="mb-2 text-4xl sm:text-5xl">🦆</div>
          <h1 className="text-2xl font-bold tracking-tight text-white">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-white/60">{subtitle}</p>}
        </div>
        {children}
      </motion.div>
    </div>
  );
}

export function Field({
  label,
  ...props
}: { label: string } & InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return (
    <label className="mb-4 block">
      <span className="mb-1.5 block text-sm font-medium text-white/70">{label}</span>
      <input
        {...props}
        className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-white outline-none transition focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/20"
      />
    </label>
  );
}

export function SubmitButton({
  children,
  loading,
  ...props
}: { children: ReactNode; loading?: boolean } & InputHTMLAttributes<HTMLButtonElement>): JSX.Element {
  return (
    <button
      type="submit"
      disabled={loading}
      {...(props as object)}
      className="mt-2 w-full rounded-xl bg-amber-400 px-4 py-2.5 font-semibold text-slate-900 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? 'Please wait…' : children}
    </button>
  );
}

export function ErrorText({ children }: { children?: ReactNode }): JSX.Element | null {
  if (!children) return null;
  return (
    <p className="mb-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
      {children}
    </p>
  );
}
